import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { acars } from "@/lib/api";
import { phaseFromTelemetry, distNm, nextDescentLow } from "@/lib/format";
import { airportLatLon } from "@/lib/airports";
import { isDesktop } from "@/lib/electron";
import { recordFlight, clearFlightView, savedFlight, seedTrail } from "@/lib/flight-session";
import SoundPlayer from "@/components/SoundPlayer";
import { soundPhase } from "@/lib/sound-order";
import { cabinNote } from "@/lib/cabin-note";

/**
 * Watches the active flight from every page: works out its phase, records the
 * phase changes and the route flown (lib/flight-session.js), and plays the
 * cabin sounds.
 *
 * Mounted once in AppLayout, so it survives navigation. Each of these used to
 * live in the Live Tracking page and died whenever the pilot left it: the
 * pushback clip stopped dead on a click to Settings, and the event log and the
 * route history started again from nothing on every visit (reported
 * 2026-10-04). Live Tracking now only reads what this records, so the phase it
 * shows is the same one that triggers the sounds.
 */
const LEVEL_FPM = 300;

function distToGo(active) {
  const dest = airportLatLon(active?.dest, active?.dest_pos);
  const lat = Number(active?.pos_lat), lon = Number(active?.pos_long);
  if (!dest || !Number.isFinite(lat) || !Number.isFinite(lon) || (lat === 0 && lon === 0)) return null;
  return distNm(lat, lon, dest[0], dest[1]);
}

export default function FlightRecorder({ onGround }) {
  // Shares the cache with AppLayout and Live Tracking. 3 s while a flight is
  // active, so a phase change is heard promptly; and in the desktop app it
  // keeps polling while the sim covers the window, which is the whole flight.
  const { data: active } = useQuery({
    queryKey: ["acars-active"],
    queryFn: acars.active,
    refetchInterval: (q) => (q.state.data?.id ? 3000 : false),
    refetchIntervalInBackground: isDesktop(),
  });

  const lastPhase = useRef(null);
  const lastFlight = useRef(null);
  // When the aircraft last became level (|vs| under LEVEL_FPM), for the climb's
  // cruise test in phaseFromTelemetry. Reset by any climb or descent.
  const levelSince = useRef(null);
  // Lowest altitude since the descent began: a level-off is not a climb.
  const descentLow = useRef(null);
  // Kept after the flight ends, so the arrival clip that is still playing is
  // not cut off when the filed flight's row disappears.
  const operator = useRef("AC");

  if (active?.id && active.id !== lastFlight.current) {
    lastFlight.current = active.id;
    // Carry the hysteresis over a restart: the record says where we were.
    // (This read the record being watched, which after a restart is none yet,
    // so it always started from no phase.)
    lastPhase.current = savedFlight(active.id).phase;
  }
  if (active?.operator) operator.current = String(active.operator).toUpperCase();

  let phase = null;
  if (active?.id) {
    const now = Date.now();
    if (Math.abs(Number(active.vs) || 0) < LEVEL_FPM) { if (levelSince.current === null) levelSince.current = now; }
    else levelSince.current = null;
    descentLow.current = nextDescentLow(descentLow.current, lastPhase.current, Number(active.alt) || 0);
    phase = phaseFromTelemetry(
      {
        alt: Number(active.alt) || 0,
        spd: Number(active.spd) || 0,
        vs: Number(active.vs) || 0,
        on_ground: onGround,
      },
      {
        time_out: active.time_out,
        time_off: active.time_off,
        time_on: active.time_on,
        time_in: active.time_in,
        cruise_ft: active.cruise_ft,
        level_secs: levelSince.current ? (now - levelSince.current) / 1000 : 0,
        // Great-circle distance to the destination, so a step down far out is
        // not the descent (format.js). Null when either end is unknown.
        dist_to_go_nm: distToGo(active),
        descent_low_ft: descentLow.current,
      },
      lastPhase.current,
    );
    lastPhase.current = phase;
  }

  const activeId = active?.id ?? null;

  // Each phase change in the app log, with what decided it (cabin-note.js).
  // A restart that finds the flight in the phase it left adds nothing.
  const notedPhase = useRef(null);
  const notedFor = useRef(null);
  useEffect(() => {
    if (activeId == null || !phase) return;
    if (notedFor.current !== activeId) { notedFor.current = activeId; notedPhase.current = null; }
    const first = notedPhase.current === null;   // first look since VACARS5 started
    const was = notedPhase.current ?? savedFlight(activeId).phase;
    notedPhase.current = phase;
    // The Live Map shows this phase too. Sent on each change, and once on the
    // first look after a start, so a restart mid-flight brings the board up to
    // date. A failed send is not retried: the next change sends again, and
    // the board works one out meanwhile.
    if (phase !== was || first) acars.phase(activeId, phase).catch(() => {});
    if (phase === was) return;
    const n = (v) => Math.round(Number(v) || 0);
    cabinNote(`phase ${was || "start"} -> ${phase} at ${n(active?.alt)} ft, ${n(active?.spd)} kt, ${n(active?.vs)} fpm, `
      + `ground ${onGround == null ? "unknown" : onGround ? "yes" : "no"} (flight ${activeId})`);
  }, [activeId, phase]);   // eslint-disable-line react-hooks/exhaustive-deps

  // The route flown before this window started watching, from the server's
  // track. Once per flight: after that the route grows from the live position.
  const { data: serverTrack } = useQuery({
    queryKey: ["acars-track", activeId],
    queryFn: () => acars.track(activeId),
    enabled: activeId != null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  useEffect(() => {
    if (activeId != null && serverTrack?.points) seedTrail(activeId, serverTrack.points);
  }, [activeId, serverTrack]);
  const lat = active?.pos_lat;
  const lon = active?.pos_long;
  useEffect(() => {
    if (activeId == null) {
      // Only once the query has answered "no flight", not while loading.
      if (active !== undefined) clearFlightView();
      return;
    }
    recordFlight(activeId, { phase, lat, lon });
  }, [activeId, phase, lat, lon, active]);

  return <SoundPlayer phase={soundPhase(phase, active)} operator={operator.current} flightId={lastFlight.current} enabled />;
}
